/* eslint-disable no-prototype-builtins */
const ANNOTATIONS = '__gt_compiler_annotations__';
const PROP_METADATA = '__gt_compiler_prop_metadata__';
const COMPONENT_CACHE = new Map<Element, ComponentType[]>();

interface ComponentConfig {
	className?: string;
	observe?: boolean;
}

export interface OnInit {
	onInit(): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComponentImplementation = any;

type ComponentType<T = ComponentImplementation> = new (
	element: HTMLElement
) => T;

interface Query {
	descendants: boolean;
	first: boolean;
	selector: ComponentType;
}

interface PropDecoratorConfig {
	descendants: boolean;
	first: boolean;
}

interface ComponentMetadata {
	type: ComponentType;
	metadataName: string;
	className: string;
	observe: boolean;
	queries: Record<string, Query>;
}

export const Component = makeDecorator('Component');

export const ComponentRef = makePropDecorator('ComponentRef', {
	first: true,
	descendants: false,
});

export const ComponentRefs = makePropDecorator('ComponentRefs', {
	first: false,
	descendants: false,
});

export const ChildRef = makePropDecorator('ChildRef', {
	first: true,
	descendants: true,
});

export const ChildrenRef = makePropDecorator('ChildrenRef', {
	first: false,
	descendants: true,
});

export function factory(componentTypes: ComponentType[]) {
	return function FactoryFunction(
		rootElement: HTMLElement | Document = document
	): void {
		const metadatas = componentTypes.map((componentType) =>
			resolve(componentType)
		);

		metadatas.forEach((metadata) => {
			compileComponent(rootElement, metadata);
		});

		addMutationObserver(
			metadatas.filter((m) => m.observe),
			(node, metadata) => compileComponent(node, metadata)
		);
	};
}

function addMutationObserver(
	metadatas: ComponentMetadata[],
	run: (node: HTMLElement, metadata: ComponentMetadata) => void
) {
	if (metadatas.length > 0) {
		new MutationObserver((mutations) =>
			mutations.forEach((mutation) =>
				metadatas.forEach((metadata) =>
					Array.from(mutation.addedNodes)
						.filter((node) => node instanceof HTMLElement)
						.forEach((node) => run(node as HTMLElement, metadata))
				)
			)
		).observe(document.body, {
			childList: true,
			subtree: true,
		});
	}
}

function makeDecorator(name: string) {
	function DecoratorFactory(config: ComponentConfig) {
		return (cls: ComponentImplementation) => {
			if (!cls.hasOwnProperty(ANNOTATIONS)) {
				Object.defineProperty(cls, ANNOTATIONS, { value: [] });
			}

			cls[ANNOTATIONS].push(config);
			return cls;
		};
	}

	DecoratorFactory.prototype.metadataName = name;
	return DecoratorFactory;
}

function throwComponentNotResolvable(componentType: ComponentType) {
	throw new Error(
		`No @Component annotation for ${
			componentType ? componentType.name : componentType
		} found!`
	);
}

function compileComponent(
	root: HTMLElement | Document,
	metadata: ComponentMetadata
) {
	const comps = Array.from(root.getElementsByClassName(metadata.className));
	if (
		root instanceof HTMLElement &&
		root.classList.contains(metadata.className)
	) {
		comps.push(root);
	}
	const initializedDatasetKey = `gtComponentInitialized${metadata.type.name}`;
	comps.forEach((node) => {
		if (Object.keys((node as HTMLElement).dataset).includes(initializedDatasetKey)) {
			return;
		}
		const constructor = new metadata.type((node as HTMLElement));
		(node as HTMLElement).dataset[initializedDatasetKey] = 'true';

		if (!COMPONENT_CACHE.has(node)) {
			COMPONENT_CACHE.set(node, []);
		}

		COMPONENT_CACHE.get(node)?.push(constructor);

		Object.keys(metadata.queries).forEach((propName) => {
			const query = metadata.queries[propName];

			const target = resolve(query.selector);

			if (!target) {
				return;
			}

			const targetNode = query.descendants ? node : document;
			const nodes = targetNode.getElementsByClassName(
				target.className
			) as HTMLCollectionOf<HTMLElement>;

			const cache = Array.from(nodes).map((e) =>
				getComponentFromElement(e, query.selector)
			);

			constructor[propName] = query.first ? cache[0] : cache;
		});

		if (typeof constructor.onInit === 'function') {
			constructor.onInit();
		}
	});
}

export function getComponentFromElement(
	element: HTMLElement,
	componentType: ComponentType<unknown>
) {
	return COMPONENT_CACHE.get(element)?.find(
		(c) => c.constructor === componentType
	);
}

function makePropDecorator(name: string, config: PropDecoratorConfig) {
	function PropDecoratorFactory(componentClass: ComponentType) {
		return function PropDecorator(
			target: ComponentImplementation,
			metaName: string
		) {
			if (!target.constructor.hasOwnProperty(PROP_METADATA)) {
				Object.defineProperty(target.constructor, PROP_METADATA, {
					value: {},
				});
			}
			const meta = target.constructor[PROP_METADATA];

			meta[metaName] = (meta.hasOwnProperty(metaName) && meta[metaName]) || [];
			meta[metaName].unshift({
				selector: componentClass,
				descendants: config.descendants,
				first: config.first,
			});
		};
	}

	PropDecoratorFactory.prototype.metadataName = name;
	return PropDecoratorFactory;
}

function annotations(ctor: ComponentType): ComponentConfig[] {
	if (ctor === null || typeof ctor !== 'function') {
		return [];
	}

	const ownAnnotations = ctor.hasOwnProperty(ANNOTATIONS)
		? ctor[ANNOTATIONS]
		: [];

	return [
		...annotations(Object.getPrototypeOf(ctor.prototype)?.constructor),
		...ownAnnotations,
	];
}

function propMetadata(ctor: ComponentType): Record<string, string[]> {
	if (ctor === null || typeof ctor !== 'function') {
		return {};
	}

	const propMetadataVal = {
		...propMetadata(Object.getPrototypeOf(ctor.prototype)?.constructor),
	};

	if (ctor.hasOwnProperty(PROP_METADATA)) {
		Object.keys(ctor[PROP_METADATA]).forEach((propName) => {
			const ownMetadata = propMetadataVal.hasOwnProperty(propName)
				? propMetadataVal[propName]
				: [];

			propMetadataVal[propName] = [
				...ownMetadata,
				...ctor[PROP_METADATA][propName],
			];
		});
	}

	return propMetadataVal;
}

function resolve(type: ComponentType): ComponentMetadata {
	const metadata = getMetadata(type);
	const queries = getQueries(type);

	return {
		type,
		metadataName: 'Component',
		className: metadata.className,
		observe: metadata.observe,
		queries,
	};
}

function getMetadata(type: ComponentType) {
	const typeMetadata = annotations(type);

	if (!typeMetadata || typeMetadata.length === 0) {
		throwComponentNotResolvable(type);
	}

	return typeMetadata[typeMetadata.length - 1];
}

function getQueries(type: ComponentType) {
	const propertyMetadata = propMetadata(type);

	return Object.keys(propertyMetadata).reduce((queries, propName) => {
		const propMetadata = propertyMetadata[propName];

		return {
			...queries,
			[propName]: propMetadata[propMetadata.length - 1],
		};
	}, {} as Record<string, Query>);
}
